import Form                    from '@widesk-ui/components/form/Form';
import MapLinksNotice          from '@forms/map/MapLinksNotice';
import ProjectModel            from '@models/map/ProjectModel';
import React                   from 'react';
import TextArea                from '@widesk-ui/components/form/TextArea';
import View                    from '@widesk-ui/components/View';
import projectPublicationStore from '@/TokamapEditor/stores/projectPublicationStore';
import publicProjectLink       from '@/tools/publicProjectLink';
import useDesignTokens         from '@/hooks/useDesignTokens';
import { message }             from '@widesk-ui/hooks/useMessage';

export type ProjectVersionPublishFormProps = {
	project: ProjectModel;
	onSuccess?: () => void;
}

/**
 * Publication d'un PROJET : une seule demande met en ligne une version de chacune de ses cartes,
 * puis le fichier condensé qui les regroupe.
 *
 * Toutes les cartes, sans exception et sans choix à faire : l'API sait n'en publier qu'une partie
 * (`maps`), mais une publication partielle laisse les autres cartes à leur version précédente, ce
 * qui se raconte mal et ne sert personne. Le formulaire ne pose donc qu'une question, facultative :
 * ce qui change dans cette version.
 */
export default function ProjectVersionPublishForm(props: ProjectVersionPublishFormProps) {
	const { project } = props;
	const tokens = useDesignTokens();
	// Le commentaire est tenu ici plutôt que dans `fields` : c'est ce qui permet de poser son
	// explication à droite de son libellé, sur la même ligne, et de resserrer l'espace qui le sépare
	// du champ. Un champ de `fields` est habillé par `FormItem`, dont le libellé n'accepte qu'une
	// chaîne et dont les marges sont celles de la grille.
	const [comment, setComment] = React.useState('');

	return (
		<Form
			successMessage={false}
			onSuccess={props.onSuccess}
			renderBeforeFields={() => (
				<View gap="xs">
					<View lineHeight={1.5}>
						Publier met en ligne l'état actuel de votre projet, à son adresse publique.
					</View>

					<View size="small" color={tokens.text.muted} lineHeight={1.5}>
						Jusque-là, vos modifications ne sont visibles que d'ici. La mise en ligne demande
						quelques instants, pendant lesquels vos visiteurs continuent de voir la publication
						précédente.
					</View>

					<View mt="xs">
						<MapLinksNotice />
					</View>
				</View>
			)}
			onSubmit={async () => {
				const version = await projectPublicationStore.publish({ project, comment: comment || undefined });

				message.info('La publication est lancée. Vous serez prévenu dès qu\'elle sera en ligne.');

				void projectPublicationStore.whenPublished(version).then(published => {
					if (published) {
						message.success(
							<View inline>
								Votre projet est en ligne.
								<a href={publicProjectLink(project)} target="_blank">
									Ouvrir l'adresse publique
								</a>
							</View>,
							8,
						);
					} else if (projectPublicationStore.lastVersion === version) {
						message.warning('La publication prend plus de temps que prévu. Rechargez la page pour suivre son avancement.', 8);
					}
				});

				return version;
			}}
		>
			<View gap={6} mb={16}>
				<View row centerV spread gap="sm">
					<View>Commentaire (optionnel)</View>

					<View size="small" color={tokens.text.muted}>Affiché dans l'historique</View>
				</View>

				<TextArea
					placeholder="Décrivez les changements apportés à cette version…"
					rows={3}
					maxLength={1500}
					showCount
					value={comment}
					onChange={e => setComment(e.target.value)}
				/>
			</View>
		</Form>
	);
}
